import { useMemo } from 'react';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * Responsive configuration contract generated by the `useResponsiveConfig` hook.
 *
 * Encapsulates widths, typography sizes, icon dimensions, spacing and padding
 * for different viewport breakpoints, to drive consistent responsive layout
 * in Competitive Intelligence dashboards.
 */
export interface ResponsiveConfig {
    // Colonnes
    countryWidth: number;
    searchTermWidth: number;
    competitionEvolutionWidth: number;
    topCompetitorsMinWidth: number;
    blendedPresenceMinWidth: number;
    
    // Typography
    fontSize: {
        header: number;
        body: number;
        caption: number;
    };
    
    // Icônes et avatars
    iconSize: {
        flag: number;
        chart: number;
        arrow: number;
        domain: number;
    };
    
    // Espacements
    spacing: {
        column: number;
        cell: number;
        padding: number;
    };
    
    // Layout
    padding: string;
    minWidth: number;
    // Competition Evolution label
    competitionEvolutionLabelMinWidth: number;
    
    // Domain Pill
    domainPill: {
        paddingX: number;
        paddingY: number;
        maxWidth: number;
        iconSize: number;
        fontSize: number;
    };
}

/**
 * Provide responsive layout values depending on the current MUI breakpoint.
 *
 * Uses `useMediaQuery` and `useTheme` to map breakpoints to a set of
 * measurements (columns, typography, icons, spacing, padding) consumed by
 * the Competitive Intelligence UI.
 *
 * @returns Memoized `ResponsiveConfig` for the active breakpoint.
 */
export const useResponsiveConfig = (): ResponsiveConfig => {
    const theme = useTheme();
    const xsUp = useMediaQuery(theme.breakpoints.up("xs"));
    const smUp = useMediaQuery(theme.breakpoints.up("sm"));
    const mdUp = useMediaQuery(theme.breakpoints.up("md"));
    const lgUp = useMediaQuery(theme.breakpoints.up("lg"));
    const xlUp = useMediaQuery(theme.breakpoints.up("xl"));

    return useMemo(() => {
        switch (true) {
            case xlUp:
                return {
                    // Colonnes
                    countryWidth: 270,
                    searchTermWidth: 270,
                    competitionEvolutionWidth: 175,
                    topCompetitorsMinWidth: 200,
                    blendedPresenceMinWidth: 400,
                    // Typography
                    fontSize: {
                        header: 14,
                        body: 14,
                        caption: 14
                    },
                    // Icônes et avatars
                    iconSize: {
                        flag: 16,
                        chart: 16,
                        arrow: 18,
                        domain: 15
                    },
                    // Espacements
                    spacing: {
                        column: 1,
                        cell: 0.5,
                        padding: 2
                    },
                    // Layout
                    padding: '16px 24px',
                    minWidth: 220,
                    competitionEvolutionLabelMinWidth: 110,
                    // Domain Pill
                    domainPill: {
                        paddingX: 1.5,
                        paddingY: 0.5,
                        maxWidth: 200,
                        iconSize: 16,
                        fontSize: 14
                    }
                };
            case lgUp:
                return {
                    countryWidth: 170,
                    searchTermWidth: 220,
                    competitionEvolutionWidth: 196,
                    topCompetitorsMinWidth: 180,
                    blendedPresenceMinWidth: 300,
                    fontSize: {
                        header: 14,
                        body: 14,
                        caption: 14
                    },
                    iconSize: {
                        flag: 16,
                        chart: 16,
                        arrow: 18,
                        domain: 13
                    },
                    spacing: {
                        column: 1,
                        cell: 0.5,
                        padding: 2
                    },
                    padding: '16px 24px',
                    minWidth: 200,
                    competitionEvolutionLabelMinWidth: 90,
                    // Domain Pill
                    domainPill: {
                        paddingX: 1.5,
                        paddingY: 0.5,
                        maxWidth: 200,
                        iconSize: 16,
                        fontSize: 14
                    }
                };
            case mdUp:
                return {
                    countryWidth: 170,
                    searchTermWidth: 200,
                    competitionEvolutionWidth: 150,
                    topCompetitorsMinWidth: 160,
                    blendedPresenceMinWidth: 180,
                    fontSize: {
                        header: 14,
                        body: 14,
                        caption: 14
                    },
                    iconSize: {
                        flag: 13,
                        chart: 14,
                        arrow: 16,
                        domain: 12
                    },
                    spacing: {
                        column: 0.5,
                        cell: 0.25,
                        padding: 1.5
                    },
                    padding: '12px 16px',
                    minWidth: 180,
                    competitionEvolutionLabelMinWidth: 60,
                    // Domain Pill
                    domainPill: {
                        paddingX: 1,
                        paddingY: 0.3,
                        maxWidth: 140,
                        iconSize: 12,
                        fontSize: 14
                    }
                };
            case smUp:
                return {
                    countryWidth: 150,
                    searchTermWidth: 180,
                    competitionEvolutionWidth: 140,
                    topCompetitorsMinWidth: 150,
                    blendedPresenceMinWidth: 120,
                    fontSize: {
                        header: 14,
                        body: 14,
                        caption: 14
                    },
                    iconSize: {
                        flag: 12,
                        chart: 14,
                        arrow: 16,
                        domain: 10
                    },
                    spacing: {
                        column: 0.5,
                        cell: 0.25,
                        padding: 1
                    },
                    padding: '8px 12px',
                    minWidth: 160,
                    competitionEvolutionLabelMinWidth: 60,
                    // Domain Pill
                    domainPill: {
                        paddingX: 1,
                        paddingY: 0.3,
                        maxWidth: 140,
                        iconSize: 10,
                        fontSize: 14
                    }
                };
            default:
                return {
                    countryWidth: 160,
                    searchTermWidth: 160,
                    competitionEvolutionWidth: 140,
                    topCompetitorsMinWidth: 120,
                    blendedPresenceMinWidth: 200,
                    fontSize: {
                        header: 14,
                        body: 14,
                        caption: 14
                    },
                    iconSize: {
                        flag: 12,
                        chart: 12,
                        arrow: 14,
                        domain: 12
                    },
                    spacing: {
                        column: 0.25,
                        cell: 0.125,
                        padding: 0.5
                    },
                    padding: '4px 8px',
                    minWidth: 140,
                    competitionEvolutionLabelMinWidth: 80,
                    // Domain Pill
                    domainPill: {
                        paddingX: 1,
                        paddingY: 0.3,
                        maxWidth: 140,
                        iconSize: 12,
                        fontSize: 14
                    }
                };
        }
    }, [xsUp, smUp, mdUp, lgUp, xlUp]);
};
